import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { WindowWrapper } from '@powerfleet/shared/common/interfaces';
import { environment } from '../environments/environment';

@Injectable({ providedIn: 'root' })
export class BrowserEnvironmentService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly windowWrapper = inject(WindowWrapper);

  readonly isBrowser = isPlatformBrowser(this.platformId);

  get storage(): Storage | null {
    if (!this.isBrowser) {
      return null;
    }

    const candidate = this.windowWrapper.localStorage;
    return this.isStorageLike(candidate) ? candidate : null;
  }

  get host(): string | null {
    if (!this.isBrowser) {
      return null;
    }

    return this.windowWrapper.location?.hostname ?? null;
  }

  get origin(): string | null {
    if (!this.isBrowser) {
      return null;
    }

    return this.windowWrapper.location?.origin ?? null;
  }

  querySelector<T extends Element>(selector: string): T | null {
    if (!this.isBrowser) {
      return null;
    }

    return this.document.querySelector(selector) as T | null;
  }

  getStorageItem(key: string): string | null {
    try {
      return this.storage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  }

  setStorageItem(key: string, value: string): void {
    try {
      this.storage?.setItem(key, value);
    } catch {
      // Some browser contexts expose storage but still block writes.
    }
  }

  resolveUrl(path: string): string | null {
    const origin = this.origin;

    if (!origin) {
      return null;
    }

    return new URL(path, origin).toString();
  }

  get gainsightPxKey(): string | null {
    if (!this.isBrowser) {
      return null;
    }

    return environment.gainsightPxKey || null;
  }

  get showLibrary(): boolean | null {
    return environment.showLibrary ?? null;
  }

  private isStorageLike(value: unknown): value is Storage {
    return !!value && typeof value === 'object' && 'getItem' in value && 'setItem' in value;
  }
}